import { useEntitlements } from "src/hooks/use-entitlement";
import { NavSection } from "src/components/layout/nav/nav-section";

import { Icon } from "src/components/layout/nav/icons/event-collection";
import { ActiveSectionProps } from "src/components/layout/nav/utils";
import { UseSectionHook } from "./types";
import { useFlags } from "launchdarkly-react-client-sdk";

// When the enableEventStudio flag is on, the Contracts link points at the
// Event Studio surface instead (one-way; Event Studio supersedes Contracts).
// Event Studio branding is internal-only (externally still visible as "Contracts").
const ContractsLink = {
  isSub: true,
  href: "/events/contracts",
  label: "Contracts",
};

const EventLibraryLink = {
  isSub: true,
  href: "/events/library",
  label: "Event library",
};

const EventStudioLink = {
  isSub: true,
  href: "/events/domains",
  activePaths: ["/events/contracts"],
  label: "Contracts",
};

const buildEventCollectionLinks = (eventStudioEnabled: boolean) => [
  ...(eventStudioEnabled ? [EventLibraryLink, EventStudioLink] : []),
  {
    isSub: true,
    href: "/events/syncs",
    label: "Event syncs",
  },
  {
    isSub: true,
    href: "/events/sources",
    label: "Event sources",
  },
  {
    isSub: true,
    href: "/events/destinations",
    label: "Event destinations",
  },
  ...(eventStudioEnabled ? [] : [ContractsLink]),
  {
    isSub: true,
    href: "/events/functions",
    label: "Functions",
  },
];

const RealtimeAudienceEnabledLinks = [
  {
    isSub: true,
    href: "/events/sources",
    label: "Event sources",
  },
];

export const useEventCollectionSection: UseSectionHook = () => {
  const { enableRealtimeAudiencesSetup, enableEventStudio } = useFlags();
  // Not `useRealtimeAudiencesEnabled`, which suspends — `<Nav />` renders
  // outside the layout's `<Suspense>` boundary. Visibility is cosmetic, so a
  // briefly-false read only costs a flicker.
  const { data: entitlementsData } = useEntitlements(false);
  const realtimeAudiencesEnabled =
    entitlementsData.entitlements.realtimeAudiences;

  return {
    title: "Event Collection",
    isEnabled:
      entitlementsData.entitlements.events ||
      realtimeAudiencesEnabled ||
      enableRealtimeAudiencesSetup,
    links: entitlementsData.entitlements.events
      ? buildEventCollectionLinks(Boolean(enableEventStudio))
      : realtimeAudiencesEnabled || enableRealtimeAudiencesSetup
        ? RealtimeAudienceEnabledLinks
        : [],
  };
};

export const EventCollection = (props: ActiveSectionProps) => {
  const section = useEventCollectionSection();
  return <NavSection icon={Icon} {...section} {...props} />;
};
